import React, {useCallback, useEffect, useMemo} from 'react';
import {
    asArray,
    assertNotNullable,
    asyncExecuteInBackground,
    evaluateFunction,
    HtmlObjectResizeMode,
    textFormatUrlForDisplay,
    validateIsUrl
} from '@wix/devzai-utils-common';
import {
    cssEllipsis,
    cssGap,
    flexColumn,
    flexColumnAlignToCenter,
    flexFillRemainingHorizontalSpace,
    flexRowAlignToStart,
    Icon, InlineLtrText,
    ListView,
    listViewCreateDataSource,
    ListViewItemRoot, reactWrapNode
} from '@wix/devzai-utils-react';
import {
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisible,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {DoppeActionTypeContactCard} from '../doppe-action-type-contact-card';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {classes, style} from './doppe-action-type-contact-card-widget.st.css';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppePageMediaView,
    DoppePageMediaViewMediaBackground
} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    DoppeDtoContactMethod,
    doppeDtoContactMethodIsValueLtr
} from '../../../client-server-common/types/doppe-dto-contact-method';
import {
    doppeViewerContactMethodResolveIcon
} from '../../../components/doppe-viewer-contact-methods/doppe-viewer-contact-methods';
import {
    DoppeActionTypeWidgetInnerTagline
} from '../../../components/doppe-action-type-widget-inner-tagline/doppe-action-type-widget-inner-tagline';
import {BymoPageLayout} from '../../../components/bymo-page-style/bymo-page-style';
import {DoppeViewerModalPanelLayout} from '../../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {
    DoppeViewerModalOpeningAnimation
} from '../../../components/doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import {CommonClasses} from '@wix/devzai-common-client';
import {
    DoppeViewerContactMethodActionsPanel
} from '../../../components/doppe-viewer-contact-method-action-panel/doppe-viewer-contact-method-action-panel';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {IDoppeViewerActionContext} from '../../../doppe-sdk';
import {
    DoppeActionCTAType,
    DoppeActionSettingsCTAButtonSettings
} from '../../../client-server-common/types/doppe-action-cta-button';
import {
    doppeListActionSettingsFilterDeletedListItemsFromActionView,
    doppeListActionSettingsFilterHiddenListItemsFromActionView
} from '../../../client-server-common';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons';
import {
    doppeDtoContactDetailsDownloadContactCard
} from "../../../client-app-and-viewer-common/doppe-dto-contact-details-client/doppe-dto-contact-details-client";

export const DoppeActionTypeContactCardWidget = React.memo(function DoppeActionTypeMembershipWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeContactCard.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        ctaButtons
    } = actionSettings;


    const visibleContactMethods = doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.contactMethods);

    const saveAsContact = useCallback((ctaSettings?: DoppeActionSettingsCTAButtonSettings.SaveContact) => {
        asyncExecuteInBackground(async () => {

            await doppeDtoContactDetailsDownloadContactCard({
                media: (ctaSettings ? doppeHideableValueGetVisibleValue(ctaSettings.saveContactProfileImage, null) : undefined) ?? actionSettings.media?.[0] ?? null,
                name: doppeHideableValueGetVisibleValue(actionSettings.name, null),
                company: doppeHideableValueGetVisibleValue(actionSettings.company, ''),
                notes: ctaSettings?.notes ?? '',
                jobTitle: doppeHideableValueGetVisibleValue(actionSettings.jobTitle, ''),
                contactMethods: doppeListActionSettingsFilterDeletedListItemsFromActionView(actionSettings.contactMethods),
            })
        }, {});
    }, [actionSettings])

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeContactCardWidgetInner
                actionContext={actionContext}
                actionId={actionContext.action.id}
                reportGoalAchieved={(goalType) => {
                    actionContext.reportGoalAchieved({
                        goalType: goalType,
                    });
                }}
                reportMethodViewed={(methodId, index) => {
                    actionContext.reportInnerItemViewed(methodId, index);
                }}
                reportMethodUsed={(methodId, index) => {
                    actionContext.reportOnlyInnerItemClicked({
                        innerItemId: methodId,
                        innerItemPosition: index,
                        goalType: DoppeActionGoalType.Click,
                    });
                }}
                openContactMethodAction={(method) => {
                    actionContext.modalsController.showInnerModal({
                        openingAnimation: bymoPageLayout => {
                            const modalPanelLayout = bymoPageLayout === BymoPageLayout.Wide ?
                                DoppeViewerModalPanelLayout.Inner :
                                DoppeViewerModalPanelLayout.Drawer;

                            switch (modalPanelLayout) {
                                case DoppeViewerModalPanelLayout.Drawer: {
                                    return DoppeViewerModalOpeningAnimation.SlideInFromBottom;
                                }
                                default: {
                                    return DoppeViewerModalOpeningAnimation.FadeIn;
                                }
                            }
                        },
                        content: <DoppeViewerContactMethodActionsPanel
                            title={doppeHideableValueGetVisibleValue(method.title, '')}
                            type={method.type}
                            displayValue={method.displayValue}
                            showNotification={(text: string) => {
                                actionContext.showNotification({
                                    message: text
                                })
                            }}
                            onClick={() => {
                                actionContext.reportGoalAchieved({
                                    goalType: DoppeActionGoalType.Click,
                                });
                                actionContext.reportOnlyInnerItemClicked({
                                    innerItemId: method.id,
                                    innerItemPosition: visibleContactMethods.findIndex(item => item.id === method.id),
                                    goalType: DoppeActionGoalType.Click,
                                });
                            }}
                            saveAsContact={() => {
                                const ctaButton = ctaButtons.filter(button => button.settings.type === DoppeActionCTAType.SaveContact)[0];
                                saveAsContact(ctaButton ? ctaButton.settings as DoppeActionSettingsCTAButtonSettings.SaveContact : undefined)
                            }}
                        />
                    });
                }}
                actionSettings={actionContext.action.settings}
                saveAsContact={saveAsContact}
            />
        </DoppeActionTypeWidgetLayout>
    );
});

export const DoppeActionTypeContactCardWidgetInner = React.memo(function DoppeActionTypeContactCardWidgetInner(
    props: {
        actionSettings: DoppeActionTypeContactCard.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        reportMethodUsed: (methodId: string, index: number) => void;
        reportMethodViewed: (methodId: string, index: number) => void;
        openContactMethodAction: (method: DoppeDtoContactMethod, saveAsContact: () => void) => void;
        actionId: string;
        saveAsContact: (ctaSettings?: DoppeActionSettingsCTAButtonSettings.SaveContact) => void;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        reportMethodViewed,
        openContactMethodAction,
        saveAsContact,
        actionContext,
        listItemId
    } = props;

    const {
        name,
        jobTitle,
        media,
        description,
        company,
    } = actionSettings;

    const calculatedCompany = doppeHideableValueGetVisibleValue(company, '');
    const calculatedJobTitle = doppeHideableValueGetVisibleValue(jobTitle, '');
    const viewerLocales = useDoppeViewerLocales();

    const visibleContactMethods = doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.contactMethods);

    const methodsItemsDataSource = useMemo(() => {
        return listViewCreateDataSource<DoppeDtoContactMethod>(visibleContactMethods, {
            idFunction: (_item, index) => index
        })
    }, [visibleContactMethods]);

    const calculatedName = evaluateFunction(() => {
        if (doppeHideableValueIsVisible(name) && (name.firstName || name.lastName)) {
            return `${name.firstName} ${name.lastName}`;
        } else {
            return '';
        }
    });

    const hasHeaderContent = !!(calculatedName || doppeHideableValueIsVisibleAndNotEqualValue(jobTitle, '') || doppeHideableValueIsVisibleAndNotEqualValue(company, '') || doppeHideableValueIsVisibleAndNotEqualValue(description, ''));

    useEffect(() => {
        visibleContactMethods.forEach((method, innerItemIndex) => {
            const innerItemId = assertNotNullable(method.id);
            reportMethodViewed(innerItemId, innerItemIndex);
        })
    }, [visibleContactMethods, reportMethodViewed]);

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
            actionContext={actionContext}
            reportGoalAchieved={reportGoalAchieved}
            mainMedia={asArray(media)[0] ?
                <div
                    className={style(classes.media, {}, flexColumnAlignToCenter())}
                >
                    <DoppePageMediaView
                        className={style(classes.productMedia, {})}
                        mediaBackground={DoppePageMediaViewMediaBackground.Auto}
                        mediaResource={asArray(media)[0]}
                        layoutSpec={{widthHeightRatio: 1}}
                        widthSpec={{width: 320}}
                        mediaResizeMode={HtmlObjectResizeMode.Cover}
                    />
                </div>

                : null}
            sections={actionSettings.sections}
            buttons={actionSettings.ctaButtons}
            buttonsSettings={{
                listItemId: listItemId,
                ctaCallback: (settings) => {
                    if (settings.type === DoppeActionCTAType.SaveContact) {
                        saveAsContact(settings);
                    }
                }
            }}
        >
            {
                calculatedName || doppeHideableValueIsVisibleAndNotEqualValue(jobTitle, '') ?
                    <div
                        className={style(classes.nameAndJobtitle, {})}
                    >
                        <DoppeActionTypeWidgetInnerTitle
                            className={style(classes.name, {})}
                        >
                            {calculatedName}
                        </DoppeActionTypeWidgetInnerTitle>
                        <DoppeActionTypeWidgetInnerTagline
                            className={style(classes.jobTitle, {})}
                        >
                            {`${calculatedJobTitle}${calculatedCompany && calculatedJobTitle ? ` ${viewerLocales['contactCard.jobAndCompanyConnectorString']} ${calculatedCompany}` : calculatedCompany ? calculatedCompany : ''}`}
                        </DoppeActionTypeWidgetInnerTagline>
                    </div>
                    :
                    null
            }
            <DoppeActionTypeWidgetInnerDescription
                className={style(classes.description, {})}
            >
                {description}
            </DoppeActionTypeWidgetInnerDescription>
            {evaluateFunction(() => {
                if (methodsItemsDataSource.length > 0) {
                    return (
                        <>
                            {
                                hasHeaderContent ?
                                    <div
                                        className={style(classes.contentSeparator, {
                                            hasHeaderTextContent: hasHeaderContent
                                        })}
                                    />
                                    :
                                    null
                            }
                            <ListView
                                className={style(classes.list, {}, flexColumn())}
                                onItemClick={(eventData) => {
                                    openContactMethodAction(eventData.dataItem, saveAsContact);
                                }}
                                renderItem={renderProps => {
                                    const {
                                        dataItem
                                    } = renderProps;

                                    return (
                                        <ListViewItemRoot
                                            className={style(classes.listItem, {}, flexRowAlignToStart(), cssGap(8), CommonClasses.button)}
                                        >
                                            <Icon
                                                className={style(classes.listItemCheckIcon, {})}
                                                icon={doppeViewerContactMethodResolveIcon(dataItem.type)}
                                            />
                                            <div
                                                className={style(classes.textsContent, {}, flexColumn(), flexFillRemainingHorizontalSpace())}
                                            >
                                                {
                                                    dataItem.title ?
                                                        <div
                                                            className={style(classes.listItemTitle, {}, cssEllipsis())}
                                                        >
                                                            {dataItem.title}
                                                        </div>
                                                        :
                                                        null
                                                }
                                                {
                                                    dataItem.displayValue ?
                                                        <div
                                                            className={style(classes.listItemValue, {}, cssEllipsis())}
                                                        >
                                                            {reactWrapNode(
                                                                validateIsUrl(dataItem.displayValue) ? textFormatUrlForDisplay(dataItem.displayValue, {
                                                                    removeProtocol: true,
                                                                    removeWWW: true
                                                                }) : dataItem.displayValue,
                                                                reactNode => {
                                                                    return doppeDtoContactMethodIsValueLtr(dataItem.type) ? (
                                                                        <InlineLtrText>
                                                                            {reactNode}
                                                                        </InlineLtrText>
                                                                    ) : reactNode;
                                                                }
                                                            )}
                                                        </div>
                                                        :
                                                        null
                                                }
                                            </div>
                                        </ListViewItemRoot>
                                    )
                                }}
                            >
                                {methodsItemsDataSource}
                            </ListView>
                        </>
                    )
                } else {
                    return null;
                }
            })}
        </DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons>
    );
});


